import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Award, Download, ExternalLink, ArrowRight } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import { downloadBlob } from "../lib/downloads";

/**
 * Public certificate share page. Has proper Open Graph + Twitter meta tags so
 * LinkedIn / Facebook / Twitter scrape the share-card PNG when the URL is
 * pasted. No auth required.
 */
export default function CertificateShare() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/certificates/verify/${certId}`)
      .then((r) => setData(r.data?.valid ? r.data : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [certId]);

  const shareCardUrl = `${API_BASE}/certificates/share-card/${certId}`;
  const ogShareUrl = `${API_BASE}/og/cert/${certId}`;
  const pageUrl = `${window.location.origin}/share/cert/${certId}`;

  const downloadCard = async () => {
    try {
      const res = await api.get(`/certificates/share-card/${certId}`, { responseType: "blob" });
      downloadBlob(res.data, `freddy-share-${certId.slice(0, 8)}.png`);
    } catch (e) { console.error("share card download", e); }
  };

  if (loading) {
    return <div className="min-h-screen bg-fc-navy flex items-center justify-center text-white/55">Loading…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="fc-overline text-fc-red mb-2">CERTIFICATE NOT FOUND</p>
          <h1 className="text-3xl font-black tracking-tight mb-3">That share link isn't valid.</h1>
          <Link to="/" className="btn-fc-red mt-4 inline-flex">Take me home</Link>
        </div>
      </div>
    );
  }

  const ogTitle = `I just completed ${data.course_title} at Freddy Compute Academy!`;
  const ogDescription = `${data.student_alias} earned a verified completion from ${data.instructor_name}. Premium AI training for automotive dealers.`;

  return (
    <div className="min-h-screen bg-fc-navy text-white" data-testid="certificate-share-page">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={shareCardUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={shareCardUrl} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <div className="text-center mb-10">
          <Award className="mx-auto text-fc-red mb-5" size={52} />
          <p className="fc-overline text-fc-red mb-3">VERIFIED COMPLETION</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">{data.course_title}</h1>
          <p className="text-white/70 text-lg">
            Earned by <strong className="text-white">{data.student_alias}</strong> from <strong className="text-white">{data.instructor_name}</strong>.
          </p>
        </div>

        <img
          src={shareCardUrl}
          alt={ogTitle}
          className="w-full border border-white/10 shadow-2xl rounded-lg mb-8"
          data-testid="share-card-image"
        />

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <button onClick={downloadCard} className="btn-fc-outline text-xs py-2.5 px-4" data-testid="download-share-card">
            <Download size={14} /> Download share card
          </button>
          <Link to={`/courses/${data.course_slug}`} className="btn-fc-red text-xs py-2.5 px-4" data-testid="cta-take-course">
            Take this course <ArrowRight size={14} />
          </Link>
          <Link to={`/verify/${certId}`} className="btn-fc-outline text-xs py-2.5 px-4">
            <ExternalLink size={14} /> Verify authenticity
          </Link>
        </div>

        <p className="text-center text-[11px] tracking-[0.22em] uppercase text-white/40 font-bold">
          FREDDY COMPUTE ACADEMY · Premium AI training for automotive dealers
        </p>
      </div>
    </div>
  );
}
